import { History } from "@mui/icons-material";
import { Typography } from "@mui/material";
import { GridColDef, GridColTypeDef } from "@mui/x-data-grid-pro";
import moment from "moment";
import { useUnHostedActivePlayersOnFloor } from "services/api/SWRService";
import { IUnHostedActivePlayer } from "services/models/Host";
import { currencyCol, numberCol, tierCol } from "utils/datagrid-utils";
import { PlayerNameCell } from "./PlayerNameCell";
import { GridPaginationProps } from "./helpers";
import { SWRDataGrid } from "./SWRDataGrid";
import { useMobile } from "hooks/Mobile/useMobile";
import { filterPanelSx } from ".";
import { SMTooltip } from "components/lib/SMTooltip";
import { PLAYER_NAME_MIN_WIDTH } from "constants/tables";
import { useCanAccess } from "@sanmanuel/permissions";
import { AppPermissions } from "constants/permissions";

export const recentActivityCol: GridColTypeDef = {
  type: "dateTime",
  minWidth: 400,
  valueGetter: ({ value }) => new Date(value),
  renderCell: ({ row }) => {
    if (row.activityLocation === null) return <Typography>N/A</Typography>;

    const [location, gameName] = row.activityLocation.split("\n");
    return (
      <Typography>
        {row.activity} - {moment(row.activityTimestamp).format("h:mm A")}
        {location != "" && <br />}
        {location}
        <br />
        {gameName}
      </Typography>
    );
  },
};

export const ActiveUnHostedPlayerOnFloorTable = (props: GridPaginationProps): JSX.Element => {
  const response = useUnHostedActivePlayersOnFloor();
  const isMobile = useMobile();
  const hasStats = useCanAccess(AppPermissions.view.playerStats);

  const protectedStats: GridColDef<IUnHostedActivePlayer>[] = [
    {
      field: "avt",
      headerName: "AvT",
      ...numberCol((v) => (v > 12 ? "12+" : v)),
    },
  ];

  const baseCols: GridColDef<IUnHostedActivePlayer>[] = [
    {
      field: "currentTier",
      headerName: "Tier",
      ...tierCol,
    },
    {
      field: "isPreviousTrip",
      headerName: "Due to Previous Trip",
      type: "boolean",
    },
    {
      field: "fullName",
      headerName: "Player",
      minWidth: PLAYER_NAME_MIN_WIDTH,
      flex: 1,
      renderCell: ({ row }) => (
        <PlayerNameCell {...row}>
          {row.isPreviousTrip && (
            <SMTooltip title="Based on most recent previous trip">
              <History color="warning" />
            </SMTooltip>
          )}
        </PlayerNameCell>
      ),
    },
    {
      field: "theoWinLoss",
      headerName: "Theo Win",
      minWidth: 120,
      ...currencyCol,
    },
    {
      field: "actualWinLoss",
      headerName: "Actual W/L",
      minWidth: 120,
      ...currencyCol,
    },

    {
      field: "activityTimestamp",
      headerName: "Recent Activity",
      minWidth: 240,
      ...recentActivityCol,
    },
    {
      field: "activity",
      headerName: "Activity",
      minWidth: 120,
    },
    {
      field: "activityLocation",
      headerName: "Activity Location",
    },
  ];

  if (hasStats) {
    baseCols.splice(5, 0, ...protectedStats);
  }

  const columns: GridColDef<IUnHostedActivePlayer>[] = baseCols;

  return (
    <SWRDataGrid
      initialState={{
        columns: {
          columnVisibilityModel: {
            isPreviousTrip: false,
            currentTier: false,
            activity: false,
            activityLocation: false,
          },
        },
        sorting: {
          sortModel: [{ field: "activityTimestamp", sort: "desc" }],
        },
      }}
      name={"unhosted-active-players"}
      response={response}
      columns={columns}
      noRowsMessage="No Active Unhosted Players on Floor"
      slotProps={{
        filterPanel: isMobile ? filterPanelSx : undefined,
      }}
      {...props}
    />
  );
};
